import React, { useContext } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { EntityTypeEnum, PermissionTypesEnum, ReservationActionTypesEnum, ReservationFlagTypeEnum } from '@api/ringil-api.v2';
import { getCalendarEventTimeData, stringYMDFromStringInTz } from '@utils/dateWithTz';
import { renderReducedItems } from '@utils/utils';
import { FlagIcon, sortFlags } from '@components/ringil3/Features/Reservation/flagIcon';
import { WorkflowStateButton } from '@components/workflow/WorkflowStateButton.tsx';
import { ApiContext } from '@api/api.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import SvgLoadUp from '@icons/LoadUp.svg?react';
import SvgUnload from '@icons/unload.svg?react';
import SvgBuilding from '@icons/building.svg?react';
import SvgClock from '@icons/Clock.svg?react';
import SvgTruck from '@icons/truck.svg?react';
import SvgGoods from '@icons/goods.svg?react';
import SvgReturnablePackaging from '@icons/returnable-packaging.svg?react';
import SvgBusiness from '@icons/business.svg?react';
import SvgNote from '@icons/note.svg?react';
import SvgWeight from '@icons/weight.svg?react';
import SvgBox from '@icons/box.svg?react';
import SvgForExpedition from '@icons/forExpedition.svg?react';
import SvgMegaphone from '@icons/megaphone.svg?react';
import { flagUnitsText } from '@components/RingilComponents/form/flagUnitsLimit';
import { FragmentRenderProps, showIcon } from './types';
import { formattedVolume, formattedWeight, orderNotes, supplierNotificationLabel } from './values';

/**
 * Detail popover renderers. Same attributes as the card but a different presentation — labels are
 * full chips rather than thin bars, rows carry an icon gutter — which is why each fragment has a
 * per-surface renderer instead of one shared component.
 *
 * The body zone spaces its rows itself (`gap-y-4`), so these carry no vertical margin.
 */

const isReturnablePackage = (data: FragmentRenderProps['data']): boolean =>
  data.flags?.some(f => f.reservationFlagType === ReservationFlagTypeEnum.RETURNABLE_PACKAGE) ?? false;

/** Reservation reference and order number. Independent of on-behalf-of, which is its own fragment. */
export const DetailName: React.FC<FragmentRenderProps> = ({ data }) => {
  const { reservationName, order } = data;
  if (!reservationName && !order?.orderId) return null;
  return (
    <div className="font-bold">
      {reservationName && (
        <span>
          {reservationName}
          {order?.orderId && <span className="inline-block px-1">|</span>}
        </span>
      )}
      {order?.orderId && <span className="whitespace-normal break-words">{order?.orderId}</span>}
    </div>
  );
};

/** The popover's counterpart of `CardTitle`: on-behalf-of, or the reference when there is none. */
export const DetailTitle: React.FC<FragmentRenderProps> = props => (props.data.onBehalfOf ? <DetailOnBehalfOf {...props} /> : <DetailName {...props} />);

export const DetailOnBehalfOf: React.FC<FragmentRenderProps> = ({ data }) => {
  const { onBehalfOf } = data;
  if (!onBehalfOf) return null;
  return <div className="font-bold whitespace-normal break-words">{onBehalfOf.name ?? onBehalfOf.externalId}</div>;
};

export const DetailDuration: React.FC<FragmentRenderProps> = ({ data }) => {
  const { tr } = useTranslationLgs();
  const timeData = getCalendarEventTimeData(data.timeslot, tr('Unit.minutesShort', 'min'));
  if (!timeData?.duration) return null;
  return <div className="whitespace-nowrap">{timeData.duration}</div>;
};

export const DetailWorkflowState: React.FC<FragmentRenderProps> = ({ data, onStateChange }) => {
  const { performTransitionOnEntity } = useContext(ApiContext);
  const { hasPermission, loggedUser } = useContext(AuthenticatedUserContext);
  const { state } = data;
  // No workflow status without a state (e.g. carrier-side calendar, where the BE returns
  // state=null): render nothing when there is no state.
  if (!state) return null;
  // Only the workflow owner (customer) may change status; the partner (carrier) sees it read-only
  // even when it holds the change permission for its own timeslot module. The same tag renders for
  // everyone; when not editable it shows without the pen and skips the transition fetch.
  const canEditStatus =
    data.customerId === loggedUser?.companyId &&
    hasPermission([PermissionTypesEnum.PERMISSION_TIMESLOT_CHANGE_STATUS, PermissionTypesEnum.PERMISSION_TIMESLOT_CHANGE_WORKFLOW]);
  return (
    <div className="inline-flex">
      <WorkflowStateButton
        state={state}
        entityId={data.reservationId}
        entityType={EntityTypeEnum.TIMESLOT}
        translationKeyPrefix={'CreateEditWorkflowStateComponent.stateName'}
        disabled={!canEditStatus}
        onStateChange={stateId => {
          performTransitionOnEntity(data.reservationId, EntityTypeEnum.TIMESLOT, { transitionToName: stateId }, () => {
            onStateChange?.();
          });
        }}
      />
    </div>
  );
};

export const DetailLabels: React.FC<FragmentRenderProps> = ({ data }) => {
  const { labels } = data;
  if (!labels || labels.length === 0) return null;
  return (
    <div className="flex flex-row flex-wrap justify-start gap-1 w-full">
      {labels.map(l => (
        <div className={clsx('rounded-[0.25rem] inline-flex justify-center items-center h-7 px-2 text-white')} key={l.labelId} style={{ backgroundColor: l.labelColor }}>
          {l.labelValue}
        </div>
      ))}
    </div>
  );
};

export const DetailActionTypes: React.FC<FragmentRenderProps> = ({ data }) => {
  const { tr } = useTranslationLgs();
  const { reservationActionTypes } = data;
  if (!reservationActionTypes || reservationActionTypes.length === 0) return null;
  return (
    <div className="flex gap-x-4 flex-row items-center flex-wrap">
      {reservationActionTypes.indexOf(ReservationActionTypesEnum.LOADING) > -1 && (
        <>
          <div className="w-4 shrink-0 flex items-center">
            <SvgLoadUp className="w-4 h-4 shrink-0 text-complementary-processing" />
          </div>
          <div>{tr(`ReservationDetail.loading`, `Loading`)}</div>
        </>
      )}
      {reservationActionTypes.indexOf(ReservationActionTypesEnum.UNLOADING) > -1 && (
        <>
          <div className="w-4 shrink-0 flex items-center">
            <SvgUnload className="w-4 h-4 shrink-0" />
          </div>
          <div>{tr(`ReservationDetail.unloading`, `Unloading`)}</div>
        </>
      )}
    </div>
  );
};

export const DetailFlags: React.FC<FragmentRenderProps> = ({ data }) => {
  const { tr } = useTranslationLgs();
  const { flags } = data;
  if (!flags || flags.length === 0) return null;
  return (
    <div className="flex gap-2 flex-row items-center flex-wrap">
      {sortFlags(flags).map(f => (
        <div key={f.reservationFlagType} className="inline-flex items-center gap-x-2 bg-content-quaternary rounded-sm px-1 py-0.5">
          <FlagIcon type={f.reservationFlagType} className="w-4 h-4 shrink-0" />
          <span>
            {tr(`ReservationFlag.${f.reservationFlagType}`, f.reservationFlagType)}
            {flagUnitsText(f) != null && tr(`CalendarEventCardDetail.flagUnits`, ` ({{units}})`, { units: flagUnitsText(f) })}
          </span>
        </div>
      ))}
    </div>
  );
};

export const DetailLocation: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const navigation = useNavigation();
  const { locationName, reservationId, reservationApplicationId } = data;
  if (!locationName) return null;
  return (
    <div className="flex flex-row items-center">
      <div className="w-8 shrink-0 flex items-center">{showIcon(config) && <SvgBuilding className={detailIconClass} />}</div>
      <div>
        {locationName}
        {reservationId && reservationApplicationId && (
          <>
            <span>{' ('}</span>
            <Link to={navigation.createNavigationLink(navigation.urlFunctions.createReservationDetail(reservationId))}>
              <span className="text-content-brand whitespace-nowrap cursor-pointer transition-all duration-200 hover:text-complementary-brandDark">{reservationApplicationId}</span>
            </Link>
            <span>{')'}</span>
          </>
        )}
      </div>
    </div>
  );
};

export const DetailCarrierInfo: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const { tr } = useTranslationLgs();
  const { carrierName, carPlate, driverInfo } = data;
  if (!carrierName && !carPlate && !driverInfo?.driverName && !driverInfo?.driverPhone) return null;
  return (
    <div className="flex flex-row items-center">
      <div className="w-8 shrink-0 flex items-center">{showIcon(config) && <SvgTruck className={detailIconClass} />}</div>
      <div className="text-xs">
        {carrierName && <div className="text-sm">{carrierName}</div>}
        {carPlate && (
          <div>
            {tr(`ReservationDetail.licensePlate`, `License plate`)}: {carPlate}
          </div>
        )}
        {(driverInfo?.driverName || driverInfo?.driverPhone) && (
          <div className="mt-4">
            {driverInfo?.driverName && <div>{driverInfo?.driverName}</div>}
            {driverInfo?.driverPhone && <div>{driverInfo?.driverPhone}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export const DetailTimeRange: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const timeData = getCalendarEventTimeData(data.timeslot);
  if (!timeData) return null;
  return (
    <div className="flex flex-row items-center">
      <div className="w-8 shrink-0 flex items-center">{showIcon(config) && <SvgClock className={detailIconClass} />}</div>
      <div>
        {timeData?.dateRange}
        <br />
        {timeData?.range}
      </div>
    </div>
  );
};

export const DetailCargoItem: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const { tr } = useTranslationLgs();
  const { orders } = data;
  const orderItems = (orders ?? []).flatMap(order => order.items);
  const returnable = isReturnablePackage(data);
  const hasItems = orderItems && orderItems.length > 0;
  if (!hasItems && !returnable) return null;
  return (
    <div className="flex flex-row items-center">
      <div className="w-8 shrink-0 flex items-center">
        {showIcon(config) && (returnable ? <SvgReturnablePackaging className={detailIconClass} /> : hasItems && <SvgGoods className={detailIconClass} />)}
      </div>
      <div>
        {hasItems && renderReducedItems(orders, tr('Unit.piecesShort', 'pcs'))}
        {hasItems && returnable && <span className="mx-1"> + </span>}
        {returnable && <>{tr(`CalendarEventCardDetail.returnablePackage`, `Returnable package`)}</>}
      </div>
    </div>
  );
};

export const DetailCreated: React.FC<FragmentRenderProps> = ({ data }) => {
  const { createdByFullName, createdAt, timezoneId } = data;
  if (!createdByFullName) return null;
  return (
    <div className="flex flex-row items-center">
      <div className="w-8 shrink-0 flex items-center">C:</div>
      <div>
        {createdByFullName} {createdAt ? stringYMDFromStringInTz(createdAt, timezoneId) : ''}
      </div>
    </div>
  );
};

export const DetailUpdated: React.FC<FragmentRenderProps> = ({ data }) => {
  const { updatedByFullName, updatedAt, timezoneId } = data;
  if (!updatedByFullName) return null;
  return (
    <div className="flex flex-row items-center">
      <div className="w-8 shrink-0 flex items-center">U:</div>
      <div>
        {updatedByFullName} {updatedAt ? stringYMDFromStringInTz(updatedAt, timezoneId) : ''}
      </div>
    </div>
  );
};

/** The detail's row shape: a fixed 2rem icon gutter, then the value. */
const DetailRow: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <div className="flex flex-row items-center">
    <div className="w-8 shrink-0 flex items-center">{icon}</div>
    <div className="whitespace-normal break-words">{children}</div>
  </div>
);

const detailIconClass = 'text-content-tertiary w-4 h-4 shrink-0';

export const DetailCustomerName: React.FC<FragmentRenderProps> = ({ data, config }) => {
  if (!data.customerName) return null;
  return <DetailRow icon={showIcon(config) && <SvgBusiness className={detailIconClass} />}>{data.customerName}</DetailRow>;
};

export const DetailOrderNote: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const notes = orderNotes(data);
  if (!notes.length) return null;
  return (
    <DetailRow icon={showIcon(config) && <SvgNote className={detailIconClass} />}>
      {notes.map((note, index) => (
        <div key={index}>{note}</div>
      ))}
    </DetailRow>
  );
};

export const DetailCargoWeight: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const weight = formattedWeight(data);
  if (!weight) return null;
  return <DetailRow icon={showIcon(config) && <SvgWeight className={detailIconClass} />}>{weight}</DetailRow>;
};

export const DetailCargoVolume: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const volume = formattedVolume(data);
  if (!volume) return null;
  return <DetailRow icon={showIcon(config) && <SvgBox className={detailIconClass} />}>{volume}</DetailRow>;
};

export const DetailLinkedExpedition: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const navigation = useNavigation();
  const coords = data.linkedToExpCoords;
  if (!coords?.applicationId) return null;
  return (
    <DetailRow icon={showIcon(config) && <SvgForExpedition className={detailIconClass} />}>
      <Link to={navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(coords.expeditionId))}>
        <span className="text-content-brand whitespace-nowrap cursor-pointer transition-all duration-200 hover:text-complementary-brandDark">{coords.applicationId}</span>
      </Link>
    </DetailRow>
  );
};

export const DetailLinkedSupplierNotification: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const label = supplierNotificationLabel(data);
  // Rendered as text rather than a link: the supplier-notification detail route needs the SN type,
  // which this DTO does not carry.
  if (!label) return null;
  return <DetailRow icon={showIcon(config) && <SvgMegaphone className={detailIconClass} />}>{label}</DetailRow>;
};

export const DetailApplicationId: React.FC<FragmentRenderProps> = ({ data }) => {
  if (!data.reservationApplicationId) return null;
  return <div className="text-xs">{data.reservationApplicationId}</div>;
};

export const DetailSlotDuration: React.FC<FragmentRenderProps> = ({ data }) => {
  const { tr } = useTranslationLgs();
  const timeData = getCalendarEventTimeData(data.timeslot, tr('Unit.minutesShort', 'min'));
  if (!timeData?.range && !timeData?.duration) return null;
  return (
    <div className="flex items-center whitespace-nowrap min-w-0">
      {timeData?.range && <span>{timeData?.range}</span>}
      <span className="inline-block text-zinc-400 leading-none">&nbsp;|&nbsp;</span>
      {timeData?.duration && <span className="truncate">{timeData?.duration}</span>}
    </div>
  );
};
